import { cn } from '@/libs/utils/styling'
import { motion } from 'framer-motion'
import { ReactNode } from 'react'

interface StatusStateProps {
  /** Soft branded icon badge content (an svg / lucide icon). */
  icon: ReactNode
  title: string
  subtitle?: ReactNode
  /** Optional action(s) rendered below the copy (e.g. buttons). */
  action?: ReactNode
  /** Extra children rendered between the copy and the action (e.g. order details). */
  children?: ReactNode
  className?: string
}

/**
 * Centred status / empty state with a soft branded icon badge.
 * Shared visual language across the Food & Drinks flow (menu search empty
 * states and the order confirmation screen).
 */
export function StatusState({
  icon,
  title,
  subtitle,
  action,
  children,
  className,
}: StatusStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={cn(
        'flex h-[70svh] flex-col items-center justify-center gap-4 px-8 text-center',
        className,
      )}
    >
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-purple7 text-purple [&>svg]:h-9 [&>svg]:w-9">
        {icon}
      </div>
      <div className="flex shrink-0 flex-col gap-1">
        <h4 className="font-body text-lg text-purple">{title}</h4>
        {subtitle && <p className="text-sm text-grey5">{subtitle}</p>}
      </div>
      {children}
      {action}
    </motion.div>
  )
}
